// src/modules/competition/components/TeamManager/index.js
import React, { useState, useRef, useEffect } from 'react';
import { ListGroup, Button, Form, Badge, InputGroup } from 'react-bootstrap';
import PropTypes from 'prop-types';

const randomId = () => crypto.randomUUID();

export const DEFAULT_TEAM_ICON = '🛡️';

export const TEAM_ICONS = [
    // Power / achievement
    '🛡️', '⚔️', '🏆', '🎯', '🚀', '⚡', '🔥', '💎',
    '🌟', '💪', '🏅', '🎖️', '👑', '🌈', '❄️', '🌊',
    // Animals
    '🦁', '🐯', '🦅', '🐺', '🐻', '🦊', '🦈', '🐲',
    '🦄', '🦋', '🦏', '🐘', '🦒', '🐆', '🦌', '🦬',
    // Sports / fitness
    '🏃', '🚴', '🏊', '🏋️', '🎿', '🏄', '🧗', '🤸',
    '⚽', '🏀', '🎾', '🏐', '🏈', '⚾', '🏒', '🥊',
    // Tech / infrastructure
    '☁️', '🖥️', '💻', '🗄️', '📡', '🛰️', '🔧', '⚙️',
    '🏢', '🏭', '💾', '🔩', '🖧', '🌐', '🔐', '🧩',
];

// ── Emoji picker ──────────────────────────────────────────────────────────────

const EmojiPicker = ({ value, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);

    return (
        <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
            <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                onClick={() => setOpen(o => !o)}
                title="Choose icon"
                style={{ fontSize: '1.1rem', lineHeight: 1, padding: '2px 8px' }}
            >
                {value || DEFAULT_TEAM_ICON}
            </Button>

            {open && (
                <div style={{
                    position: 'absolute',
                    zIndex: 1055,
                    top: 'calc(100% + 4px)',
                    left: 0,
                    background: 'var(--bs-body-bg)',
                    border: '1px solid var(--bs-border-color)',
                    borderRadius: '8px',
                    padding: '8px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(8, 1fr)',
                    gap: '2px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                    minWidth: '240px',
                }}>
                    {TEAM_ICONS.map(emoji => (
                        <button
                            key={emoji}
                            type="button"
                            onClick={() => { onChange(emoji); setOpen(false); }}
                            title={emoji}
                            style={{
                                background: value === emoji ? 'var(--bs-primary)' : 'transparent',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '1.25rem',
                                padding: '4px',
                                lineHeight: 1,
                                transition: 'background 0.1s',
                            }}
                        >
                            {emoji}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// ── TeamManager ───────────────────────────────────────────────────────────────

const TeamManager = ({ teams, onChange }) => {
    const [newName, setNewName] = useState('');
    const [newIcon, setNewIcon] = useState(DEFAULT_TEAM_ICON);
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const [editingIcon, setEditingIcon] = useState(DEFAULT_TEAM_ICON);

    const isDuplicateName = (name, excludeId = null) =>
        teams.some(t => t.teamId !== excludeId && t.name.toLowerCase() === name.toLowerCase().trim());

    const handleAdd = () => {
        const name = newName.trim();
        if (!name || isDuplicateName(name)) return;
        onChange([...teams, { teamId: randomId(), name, icon: newIcon, members: [] }]);
        setNewName('');
        setNewIcon(DEFAULT_TEAM_ICON);
    };

    const startEdit = (team) => {
        setEditingId(team.teamId);
        setEditingName(team.name);
        setEditingIcon(team.icon || DEFAULT_TEAM_ICON);
    };

    const commitEdit = (teamId) => {
        const name = editingName.trim();
        if (!name || isDuplicateName(name, teamId)) { cancelEdit(); return; }
        onChange(teams.map(t => t.teamId === teamId ? { ...t, name, icon: editingIcon } : t));
        setEditingId(null);
    };

    const cancelEdit = () => { setEditingId(null); };

    const handleDelete = (teamId) => onChange(teams.filter(t => t.teamId !== teamId));

    return (
        <div>
            {teams.length > 0 && (
                <ListGroup className="mb-2">
                    {teams.map(team => (
                        <ListGroup.Item key={team.teamId} className="d-flex align-items-center gap-2 py-2">
                            {editingId === team.teamId ? (
                                <>
                                    <EmojiPicker value={editingIcon} onChange={setEditingIcon} />
                                    <Form.Control
                                        size="sm"
                                        value={editingName}
                                        onChange={e => setEditingName(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') { e.preventDefault(); commitEdit(team.teamId); }
                                            if (e.key === 'Escape') cancelEdit();
                                        }}
                                        autoFocus
                                        maxLength={50}
                                        className="flex-grow-1"
                                        isInvalid={isDuplicateName(editingName, team.teamId) && editingName.trim() !== ''}
                                    />
                                    <Button size="sm" variant="success" onClick={() => commitEdit(team.teamId)} title="Save">
                                        <i className="bi bi-check-lg"></i>
                                    </Button>
                                    <Button size="sm" variant="outline-secondary" onClick={cancelEdit} title="Cancel">
                                        <i className="bi bi-x-lg"></i>
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <span style={{ fontSize: '1.2rem', lineHeight: 1, flexShrink: 0 }}>
                                        {team.icon || DEFAULT_TEAM_ICON}
                                    </span>
                                    <span className="flex-grow-1">{team.name}</span>
                                    {team.members?.length > 0 && (
                                        <Badge bg="secondary" className="me-1">
                                            {team.members.length} member{team.members.length !== 1 ? 's' : ''}
                                        </Badge>
                                    )}
                                    <Button size="sm" variant="outline-secondary" onClick={() => startEdit(team)} title="Edit">
                                        <i className="bi bi-pencil"></i>
                                    </Button>
                                    <Button size="sm" variant="outline-danger" onClick={() => handleDelete(team.teamId)} title="Delete">
                                        <i className="bi bi-trash"></i>
                                    </Button>
                                </>
                            )}
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            )}

            <div>
                <InputGroup size="sm">
                    <EmojiPicker value={newIcon} onChange={setNewIcon} />
                    <Form.Control
                        placeholder="New team name…"
                        value={newName}
                        onChange={e => setNewName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
                        maxLength={50}
                        isInvalid={isDuplicateName(newName) && newName.trim() !== ''}
                    />
                    <Button
                        type="button"
                        variant="outline-primary"
                        onClick={handleAdd}
                        disabled={!newName.trim() || isDuplicateName(newName)}
                    >
                        <i className="bi bi-plus-lg me-1"></i>Add Team
                    </Button>
                </InputGroup>
                {isDuplicateName(newName) && newName.trim() !== '' && (
                    <Form.Text className="text-danger">A team with that name already exists.</Form.Text>
                )}
            </div>
        </div>
    );
};

TeamManager.propTypes = {
    teams: PropTypes.arrayOf(PropTypes.shape({
        teamId: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        icon: PropTypes.string,
        members: PropTypes.arrayOf(PropTypes.string),
    })).isRequired,
    onChange: PropTypes.func.isRequired,
};

export default TeamManager;
